import {
  centsToDollars,
  dollarsToCents,
  dollarsToMicros,
  UsdDollars,
} from "@redotech/money/usd-amounts";
import { useRequiredContext } from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useHandler } from "@redotech/react-util/hook";
import {
  ORDER_TRACKING_BILLING_DEFAULTS,
  USD_TO_MICROS,
} from "@redotech/redo-billing-common/billing-defaults";
import { PaymentProviderType } from "@redotech/redo-billing-common/billing-engine-types";
import { defaultBrandKit } from "@redotech/redo-content-builder-common/brand-kit";
import { Category } from "@redotech/redo-flows-common/triggers";
import { UpsellProductSource } from "@redotech/redo-order-tracking-settings-common/upsell";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { PageForm, PageFormSave } from "@redotech/redo-web/page-form";
import { getReviewsFormDefault } from "@redotech/redo-web/reviews-form/review-form-types";
import { groupInput, InputSchema } from "@redotech/ui/form";
import classNames from "classnames";
import { memo, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import { RedoAdminClientContext } from "../../client/context";
import { getTeam, updateTeam } from "../../client/team";
import { LoadTeamContext, TeamContext } from "../team";
import * as teamCss from "../team.module.css";
import {
  AdvancedTrackerSettingsCard,
  advancedTrackerSettingsDefault,
  advancedTrackerSettingsForm,
} from "./orders/advanced-tracker-settings";
import {
  ImportStripeInfoCard,
  importStripeInfoForm,
} from "./orders/import-stripe-info-card";
import {
  OrderTrackingGeneralCard,
  orderTrackingGeneralDefault,
  orderTrackingGeneralForm,
  ShipmentTrackingCard,
} from "./orders/order-tracking";
import {
  OrderTrackingBillingCard,
  orderTrackingBillingDefault,
  orderTrackingBillingForm,
} from "./orders/order-tracking-billing";
import {
  OrderTrackingV2Card,
  orderTrackingV2Default,
  orderTrackingV2Form,
} from "./orders/order-tracking-v2";
import {
  ReviewsGeneralCard,
  reviewsGeneralDefault,
  reviewsGeneralForm,
} from "./orders/reviews";
import { ShipmentTrackingModal } from "./orders/shipment-tracking-modal";

export const OrderTrackingPage = memo(function OrderTrackingPage() {
  const params = useParams();
  const teamId = params.teamId!;

  const team = useContext(TeamContext);
  const loadTeam = useRequiredContext(LoadTeamContext);

  const handleSave = useHandler(() => {
    loadTeam();
  });

  const value = useMemo<OrderTrackingValue | undefined>(() => {
    if (!team) {
      return;
    }

    const orderTrackingBilling = team.settings?.orderTracking?.billing;
    return {
      general: {
        enabled: team.settings?.orderTracking?.enabled ?? false,
        createTrackers: team.settings?.orderTracking?.createTrackers ?? false,
        useRedoTrackingUrl:
          team.settings?.orderTracking?.useRedoTrackingUrl ?? false,
        excludeTrackMyOrderClicks:
          team.settings?.orderTracking?.excludeTrackMyOrderClicks ?? false,
        smsEnabled: team.settings?.orderTracking?.smsEnabled ?? false,
        skipSmsOptInRequirement:
          team.settings?.orderTracking?.skipSmsOptInRequirement ?? false,
      },
      billing: orderTrackingBilling
        ? ({
            enabled: orderTrackingBilling.enabled,
            invoiceBilling: {
              enabled: orderTrackingBilling.invoiceBilling?.enabled ?? false,
              paymentProvider:
                orderTrackingBilling.invoiceBilling?.paymentProvider ??
                PaymentProviderType.SHOPIFY,
              renewOnPeriodEnd:
                orderTrackingBilling.invoiceBilling?.renewOnPeriodEnd ?? true,
              startDate: orderTrackingBilling.invoiceBilling?.startDate
                ? new Date(orderTrackingBilling.invoiceBilling.startDate)
                : null,
              period:
                orderTrackingBilling.invoiceBilling?.period ??
                ORDER_TRACKING_BILLING_DEFAULTS.PERIOD,
              periodFeeUsd: orderTrackingBilling.invoiceBilling
                ?.periodFeeInCents
                ? centsToDollars(
                    orderTrackingBilling.invoiceBilling?.periodFeeInCents,
                  )
                : ORDER_TRACKING_BILLING_DEFAULTS.PERIOD_FEE_USD,
              perOrderSettings: {
                enabled:
                  orderTrackingBilling.invoiceBilling?.perOrderSettings
                    .enabled ?? false,
                includedOrders:
                  orderTrackingBilling.invoiceBilling?.perOrderSettings
                    .includedOrders ??
                  ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_ORDERS,
                overagePricePerOrderUsd:
                  (orderTrackingBilling.invoiceBilling?.perOrderSettings
                    .overagePricePerOrderMicros ??
                    ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_ORDER_MICROS) /
                  USD_TO_MICROS,
              },
              perSmsSettings: {
                enabled:
                  orderTrackingBilling.invoiceBilling?.perSmsSettings.enabled ??
                  false,
                includedCredits:
                  orderTrackingBilling.invoiceBilling?.perSmsSettings
                    .includedCredits ??
                  ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_CREDITS,
                overagePricePerSmsUsd:
                  (orderTrackingBilling.invoiceBilling?.perSmsSettings
                    .overagePricePerSmsMicros ??
                    ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_SMS_MICROS) /
                  USD_TO_MICROS,
                overagePricePerMmsUsd:
                  (orderTrackingBilling.invoiceBilling?.perSmsSettings
                    .overagePricePerMmsMicros ??
                    ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_MMS_MICROS) /
                  USD_TO_MICROS,
                chargeCarrierFees:
                  !orderTrackingBilling.invoiceBilling?.perSmsSettings
                    .ignoreCarrierFees,
              },
              perEmailSettings: {
                enabled:
                  orderTrackingBilling.invoiceBilling?.perEmailSettings
                    .enabled ?? false,
                includedEmails:
                  orderTrackingBilling.invoiceBilling?.perEmailSettings
                    .includedEmails ??
                  ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_EMAILS,
                overagePricePerEmailUsd:
                  (orderTrackingBilling.invoiceBilling?.perEmailSettings
                    .overagePricePerEmailMicros ??
                    ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_EMAIL_MICROS) /
                  USD_TO_MICROS,
              },
              perShipmentSettings: {
                enabled:
                  orderTrackingBilling.invoiceBilling?.perShipmentSettings
                    ?.enabled ?? false,
                includedShipments:
                  orderTrackingBilling.invoiceBilling?.perShipmentSettings
                    ?.includedShipments ??
                  ORDER_TRACKING_BILLING_DEFAULTS.INCLUDED_SHIPMENTS,
                overagePricePerShipmentUsd:
                  (orderTrackingBilling.invoiceBilling?.perShipmentSettings
                    ?.overagePricePerShipmentMicros ??
                    ORDER_TRACKING_BILLING_DEFAULTS.OVERAGE_PRICE_PER_SHIPMENT_MICROS) /
                  USD_TO_MICROS,
              },
              startDateIsUpdatedToThePast: false,
            },
            continueIfNotAccepted: orderTrackingBilling.continueIfNotAccepted,
            pricePerOrder: orderTrackingBilling?.pricePerOrder.toString(),
            freeOrdersPerMonth:
              orderTrackingBilling?.freeOrdersPerMonth.toString(),
            freeUntilDateEnabled:
              orderTrackingBilling?.freeUntilDateEnabled || false,
            freeUntilDate: orderTrackingBilling?.freeUntilDate || "",
            texts: {
              pricePerSms:
                orderTrackingBilling.texts?.pricePerSms.toString() ?? "0.01",
              pricePerMms:
                orderTrackingBilling.texts?.pricePerMms.toString() ?? "0.03",
            },
            rerouteBillingToMarketing:
              orderTrackingBilling.rerouteBillingToMarketing || false,
          } satisfies OrderTrackingValue["billing"])
        : orderTrackingBillingDefault,
      reviews: { enabled: team.settings?.reviews?.enabled ?? false },
      advancedTrackerSettings: {
        alwaysPreserveCarrierInTrackerSyncs:
          team.settings?.orderTracking?.alwaysPreserveCarrierInTrackerSyncs ??
          false,
        keepRetryingTrackerSyncsForAtLeastHours:
          team.settings?.orderTracking
            ?.keepRetryingTrackerSyncsForAtLeastHours ?? null,
      },
      importStripeInfo: { stripeCustomerId: "" },
      v2: {
        enabled: team.settings?.orderTracking?.v2?.enabled ?? false,
        setTrackingUrl:
          team.settings?.orderTracking?.v2?.setTrackingUrl ?? false,
        setTrackingUrlOnSource:
          team.settings?.orderTracking?.v2?.setTrackingUrlOnSource ?? false,
        dispatchEnabled:
          team.settings?.orderTracking?.v2?.dispatchEnabled ?? false,
        defaultTrackingPageUrl:
          team.settings?.orderTracking?.v2?.defaultTrackingPageUrl ?? "",
        backfillDays: "30",
      },
    } satisfies OrderTrackingValue;
  }, [team]);

  return (
    <OrderTrackingForm
      id={teamId}
      key={+!team}
      onSave={handleSave}
      value={value}
    />
  );
});

const orderTrackingForm = groupInput({
  general: orderTrackingGeneralForm,
  billing: orderTrackingBillingForm,
  reviews: reviewsGeneralForm,
  advancedTrackerSettings: advancedTrackerSettingsForm,
  importStripeInfo: importStripeInfoForm,
  v2: orderTrackingV2Form,
});

type OrderTrackingForm = InputSchema.Input<typeof orderTrackingForm>;

type OrderTrackingValue = InputSchema.Value<typeof orderTrackingForm>;

const orderTrackingDefault: OrderTrackingValue = {
  general: orderTrackingGeneralDefault,
  billing: orderTrackingBillingDefault,
  reviews: reviewsGeneralDefault,
  advancedTrackerSettings: advancedTrackerSettingsDefault,
  importStripeInfo: { stripeCustomerId: "" },
  v2: orderTrackingV2Default,
};

const OrderTrackingForm = memo(function OrderTrackingForm({
  onSave,
  id,
  value,
}: {
  id: string;
  value: OrderTrackingValue | undefined;
  onSave?: (value: OrderTrackingValue) => void;
}) {
  const client = useRequiredContext(RedoAdminClientContext);
  const adminRpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const input = useInput(orderTrackingForm, value || orderTrackingDefault);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);

  const save: PageFormSave = useHandler(async (signal) => {
    const team = await getTeam(client, { teamId: id, signal });
    const value = input.value;

    const orderTrackingEnabledPreviously =
      !!team.settings.orderTracking?.enabled;
    const reviewsEnabledPreviously = !!team.settings.reviews?.enabled;
    team.settings.orderTracking = {
      ...team.settings.orderTracking,
      enabled: value.general.enabled,
      enabledAt: value.general.enabled
        ? new Date()
        : team.settings.orderTracking?.enabledAt,
      firstEnabledAt:
        !orderTrackingEnabledPreviously &&
        value.general.enabled &&
        !team.settings.orderTracking?.firstEnabledAt
          ? new Date()
          : team.settings.orderTracking?.firstEnabledAt,
      smsEnabled: value.general.smsEnabled,
      billing: {
        // there is a slight possibility of a race condition:
        // 1. cron updates activeSubscriptions
        // 2. this form is submitted, overwriting the newer data from webhook
        // But this is unlikely, so... #OstrichAlgorithm
        // https://en.wikipedia.org/wiki/Ostrich_algorithm
        ...team.settings.orderTracking?.billing,
        ...value.billing,
        invoiceBilling: {
          ...team.settings.orderTracking?.billing?.invoiceBilling,
          enabled: value.billing.invoiceBilling.enabled,
          paymentProvider: value.billing.invoiceBilling.paymentProvider,
          startDate: value.billing.invoiceBilling.startDate ?? undefined,
          period: value.billing.invoiceBilling.period,
          periodFeeInCents: dollarsToCents(
            value.billing.invoiceBilling.periodFeeUsd as UsdDollars,
          ),
          renewOnPeriodEnd: value.billing.invoiceBilling.renewOnPeriodEnd,
          perOrderSettings: {
            ...team.settings.orderTracking?.billing?.invoiceBilling
              ?.perOrderSettings,
            enabled: value.billing.invoiceBilling.perOrderSettings.enabled,
            includedOrders: Number(
              value.billing.invoiceBilling.perOrderSettings.includedOrders,
            ),
            overagePricePerOrderMicros: dollarsToMicros(
              Number(
                value.billing.invoiceBilling.perOrderSettings
                  .overagePricePerOrderUsd,
              ) as UsdDollars,
            ),
          },
          perShipmentSettings: {
            ...team.settings.orderTracking?.billing?.invoiceBilling
              ?.perShipmentSettings,
            enabled: value.billing.invoiceBilling.perShipmentSettings.enabled,
            includedShipments: Number(
              value.billing.invoiceBilling.perShipmentSettings
                .includedShipments,
            ),
            overagePricePerShipmentMicros: dollarsToMicros(
              Number(
                value.billing.invoiceBilling.perShipmentSettings
                  .overagePricePerShipmentUsd,
              ) as UsdDollars,
            ),
          },
          perSmsSettings: {
            ...team.settings.orderTracking?.billing?.invoiceBilling
              ?.perSmsSettings,
            enabled: value.billing.invoiceBilling.perSmsSettings.enabled,
            includedCredits: Number(
              value.billing.invoiceBilling.perSmsSettings.includedCredits,
            ),
            overagePricePerSmsMicros: dollarsToMicros(
              Number(
                value.billing.invoiceBilling.perSmsSettings
                  .overagePricePerSmsUsd,
              ) as UsdDollars,
            ),
            overagePricePerMmsMicros: dollarsToMicros(
              Number(
                value.billing.invoiceBilling.perSmsSettings
                  .overagePricePerMmsUsd,
              ) as UsdDollars,
            ),
            ignoreCarrierFees:
              !value.billing.invoiceBilling.perSmsSettings.chargeCarrierFees,
          },
          perEmailSettings: {
            ...team.settings.orderTracking?.billing?.invoiceBilling
              ?.perEmailSettings,
            enabled: value.billing.invoiceBilling.perEmailSettings.enabled,
            includedEmails: Number(
              value.billing.invoiceBilling.perEmailSettings.includedEmails,
            ),
            overagePricePerEmailMicros: dollarsToMicros(
              Number(
                value.billing.invoiceBilling.perEmailSettings
                  .overagePricePerEmailUsd,
              ) as UsdDollars,
            ),
          },
        },
        pricePerOrder: Number(value.billing.pricePerOrder),
        freeOrdersPerMonth: Number(value.billing.freeOrdersPerMonth),
        freeUntilDateEnabled: value.billing.freeUntilDateEnabled,
        freeUntilDate: value.billing.freeUntilDate,
        texts: {
          pricePerSms: Number(value.billing.texts.pricePerSms),
          pricePerMms: Number(value.billing.texts.pricePerMms),
        },
        continueIfNotAccepted: value.billing.continueIfNotAccepted,
        rerouteBillingToMarketing: value.billing.rerouteBillingToMarketing,
      },
      createTrackers: value.general.createTrackers,
      useRedoTrackingUrl: value.general.useRedoTrackingUrl,
      excludeTrackMyOrderClicks: value.general.excludeTrackMyOrderClicks,
      skipSmsOptInRequirement: value.general.skipSmsOptInRequirement,
      alwaysPreserveCarrierInTrackerSyncs:
        value.advancedTrackerSettings.alwaysPreserveCarrierInTrackerSyncs,
      keepRetryingTrackerSyncsForAtLeastHours:
        value.advancedTrackerSettings.keepRetryingTrackerSyncsForAtLeastHours,
      v2: {
        ...team.settings.orderTracking?.v2,
        enabled: value.v2.enabled,
        setTrackingUrl: value.v2.setTrackingUrl,
        setTrackingUrlOnSource: value.v2.setTrackingUrlOnSource,
        dispatchEnabled: value.v2.dispatchEnabled,
        defaultTrackingPageUrl: value.v2.defaultTrackingPageUrl || undefined,
      },
    };
    team.settings.reviews = { enabled: value.reviews.enabled };

    if (value.general.enabled && !team.settings.orderTracking?.upsellProducts) {
      team.settings.orderTracking!.upsellProducts = {
        source: UpsellProductSource.RECOMMENDATIONS,
      };
    }

    const createDefaults: Category[] = [];
    if (value.general.enabled && !orderTrackingEnabledPreviously) {
      createDefaults.push("Order tracking");
    }

    for (const category of createDefaults) {
      await adminRpcClient.createDefaultFlowsAndTemplates({
        teamId: id,
        category,
      });
    }

    if (value.reviews.enabled && !reviewsEnabledPreviously) {
      const brandKit = team.settings.brandKit || defaultBrandKit;
      const defaultFormData = getReviewsFormDefault(brandKit);

      await adminRpcClient.createDefaultReviewForm({
        teamId: id,
        formData: defaultFormData,
      });
    }

    await updateTeam(client, { teamId: id, team, signal });
    onSave?.(value);
  });

  const { general } = input.inputs;

  return (
    <div className={teamCss.container}>
      <Flex dir="column" gap="lg">
        <PageForm
          initial={value || orderTrackingDefault}
          input={input}
          save={save}
        >
          <div className={gridCss.grid}>
            <div className={classNames(gridCss.span12, gridCss.span6L)}>
              <div className={gridCss.grid}>
                <div className={gridCss.span12}>
                  <OrderTrackingGeneralCard input={general} />
                </div>
                <div className={gridCss.span12}>
                  <ShipmentTrackingCard
                    createTrackersInput={
                      input.inputs.general.inputs.createTrackers
                    }
                    onAnalyzeTracking={() => setTrackingModalOpen(true)}
                    orderTrackingEnabled={input.value.general.enabled}
                  />
                </div>
                <div className={gridCss.span12}>
                  <ImportStripeInfoCard
                    stripeCustomerIdInput={
                      input.inputs.importStripeInfo.inputs.stripeCustomerId
                    }
                  />
                </div>
                <div className={gridCss.span12}>
                  <OrderTrackingV2Card
                    input={input.inputs.v2}
                    perShipmentBillingEnabled={
                      input.value.billing.invoiceBilling.perShipmentSettings
                        .enabled
                    }
                  />
                </div>
                <div className={gridCss.span12}>
                  <ReviewsGeneralCard input={input.inputs.reviews} />
                </div>
              </div>
            </div>
            <div className={classNames(gridCss.span12, gridCss.span6L)}>
              <div className={gridCss.grid}>
                <div className={gridCss.span12}>
                  <OrderTrackingBillingCard
                    input={input.inputs.billing}
                    v2Enabled={input.value.v2.enabled ?? false}
                  />
                </div>
                <div className={gridCss.span12}>
                  <AdvancedTrackerSettingsCard
                    input={input.inputs.advancedTrackerSettings}
                  />
                </div>
              </div>
            </div>
          </div>
        </PageForm>
      </Flex>
      <ShipmentTrackingModal
        onClose={() => setTrackingModalOpen(false)}
        open={trackingModalOpen}
        teamId={id}
      />
    </div>
  );
});
